/////////////
// Imports //
/////////////

// Three.js:
import * as THREE from 'three';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from 'three-mesh-bvh';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import Stats from 'three/examples/jsm/libs/stats.module.js';

// Patch raycasting
THREE.Mesh.prototype.raycast = acceleratedRaycast;

// Add BVH helpers
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

// ImGUI:

// Custom:
import { setCamera, setPostProcess, setRenderer, setScene, worldStatus } from '../Global/global_variables.js';

import { PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline.js';
import useNotificationStore from '@gui/store/notification.js';
import { InstanceRenderer } from '../Graphics/InstanceRenderer.js';
import { ShadowBuilder } from '../Graphics/ShadowBuilder.js';
import { StandardRenderer } from '../Graphics/standardRenderer.js';
import { VisualizationElements } from '../Graphics/VisualizationElements.js';
import usePersistentStore, { getColorScheme, getGlobalState } from '../Gui/store/persistent/persistentStore.js';
import { getGrid, LoadingStatus, setLoadingStatus } from '../store/LoadingStore.js';
import { CameraHandler } from './camera/CameraHelper.js';
import { PostProcess } from './PostProcessing.js';

import { ControllerManager } from '@gui/controller/ControllerManager.js';
import { NotificationType } from '@visualizer/types';
import type { GeoEditManager } from '@data/geodata/edit/GeoEditManager';
import type { SelectMode } from '@data/geodata/edit/SelectMode.js';
import RaysHandler from '../Simulations/RaysHandler.js';
import { cacheCanvasFrame } from '../sentryAttachments';

let lastTime = 0; // Time of the last frame

let dragging = false;

export const stats = new Stats();

/////////////////////////////////
// Body of Class Visualization //
/////////////////////////////////

export class Visualization {
    // CONTEXT
    readonly canvas: HTMLCanvasElement;

    //// CAMERA

    readonly cameraHelper: CameraHandler;

    // RENDERING
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    private _instanceRenderer?: InstanceRenderer;
    private standardRenderer?: StandardRenderer;
    private transformControls?: TransformControls;

    // HELPERS
    postProcess: PostProcess;
    shadowBuilder?: ShadowBuilder;

    // STATS
    /**
     * @deprecated move it in viewer store
     */
    fps: number = 60;
    private renderCount = 0;

    // Geo editing
    geoEditManager?: GeoEditManager;
    selectMode?: SelectMode;

    // Delta time in seconds
    private _deltaSec: number = 0;
    private renderRequested = false;
    private rafId: number | null = null;
    private continuousRender = false;
    private continuousRenderReasons = new Set<string>();
    private burstUntil = 0;
    private burstFps = 10;
    private burstTimeoutId: number | null = null;
    private activityEventsBound = false;
    private pipelineUnsub: (() => void) | null = null;
    private raysUnsub: (() => void) | null = null;
    private renderReady = false;
    private lastSentryFrameCacheTs = 0;
    private static readonly SENTRY_FRAME_CACHE_INTERVAL_MS = 2000;

    public readonly requestRenderIfNotRequested = () => {
        if (!this.renderReady || this.disposed || this.renderRequested) return;
        this.renderRequested = true;
        if (this.burstTimeoutId != null) {
            clearTimeout(this.burstTimeoutId);
            this.burstTimeoutId = null;
        }
        this.rafId = requestAnimationFrame(this.renderFrame);
    };
    public setContinuousRender(enabled: boolean) {
        this.setContinuousRenderReason('manual', enabled);
    }

    // Function useful for the future if multiple systems uses continous render
    public setContinuousRenderReason(reason: string, enabled: boolean) {
        if (enabled) {
            this.continuousRenderReasons.add(reason);
        } else {
            this.continuousRenderReasons.delete(reason);
        }
        const next = this.continuousRenderReasons.size > 0;
        if (this.continuousRender !== next) {
            this.continuousRender = next;
            if (next) {
                this.requestRenderIfNotRequested();
            }
        }
    }
    //Create time of burst and call the fps pacer
    public requestRenderBurst(durationMs: number = 350, fps: number = 10) {
        if (!this.renderReady || this.disposed) return;
        const now = performance.now();
        this.burstUntil = Math.max(this.burstUntil, now + durationMs);
        this.burstFps = fps;
        if (!this.continuousRender && !this.renderRequested && this.rafId == null) {
            this.scheduleBurstRender();
        }
    }
    //controls: smooth rendering for camera, ui: burst of fps to re-render changes
    public requestRender(reason: 'controls' | 'ui' = 'ui') {
        if (reason === 'controls') {
            this.requestRenderIfNotRequested();
        } else {
            this.requestRenderBurst();
        }
    }
    private readonly renderFrame = (time: number) => {
        if (this.disposed) return;
        this.renderRequested = false;
        this.rafId = null;
        try {
            this.loop(time);
        } catch (e) {
            console.error(e);
            this.renderer.setAnimationLoop(null); // Ensure any loop is stopped if there is an error
            this.done();
            useNotificationStore
                .getState()
                .showNotification('An error occurred during rendering. Please check the console for details.', NotificationType.Error);
            setLoadingStatus(LoadingStatus.ERROR);
        }
    };
    private readonly handleResize = () => {
        this.updateCanvasSize();
        this.requestRenderIfNotRequested();
    };
    private readonly handleBeforeUnload = () => this.done();
    private readonly handleControlRender = () => {
        this.requestRender('controls');
    };
    private readonly handleUiRender = () => {
        this.requestRender('ui');
    };
    private readonly handleUiPointerDown = () => {
        this.setContinuousRenderReason('ui-drag', true);
        this.requestRender('controls');
    };
    private readonly handleUiPointerUp = () => {
        this.setContinuousRenderReason('ui-drag', false);
        this.requestRender('ui');
    };
    private readonly attachCanvasToContainer = () => {
        if (this.disposed) return;

        const container = document.getElementById('canvas-container');
        if (!container) {
            requestAnimationFrame(this.attachCanvasToContainer);
            return;
        }

        if (this.canvas.parentElement !== container) {
            container.appendChild(this.canvas);
        }
    };
    private disposed = false;

    constructor() {
        // Update raycaster to make it faster:
        THREE.Mesh.prototype.raycast = acceleratedRaycast;
        THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
        THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
        console.log('BVH raycast active:', THREE.Mesh.prototype.raycast === acceleratedRaycast);

        // Three.js:
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            powerPreference: 'high-performance',
            depth: true,
            preserveDrawingBuffer: false,
        });
        this.canvas = renderer.domElement;
        this.canvas.id = 'canvas';

        renderer.setSize(window.innerWidth * window.devicePixelRatio, window.innerHeight * window.devicePixelRatio, false);
        this.renderer = renderer;
        const scene = new THREE.Scene();

        this.scene = scene;
        this.scene.rotation.x = -Math.PI / 2;
        this.scene.up = new THREE.Vector3(0, 0, 1);

        this.cameraHelper = new CameraHandler(this);

        this.postProcess = new PostProcess(renderer, this);
        this.bindRenderEvents();

        // Once renderer, cameraHelper and postProcess are set, init window (avoids issues with resize before initialization):
        this.initWindow();
    }

    public setStatsVisibility(visible: boolean) {
        if (import.meta.env.MODE !== 'development') return; // avoid showing in prod

        if (visible) {
            if (!stats.dom.parentNode) {
                document.body.appendChild(stats.dom);
            }
        } else {
            if (stats.dom.parentNode) {
                stats.dom.parentNode.removeChild(stats.dom);
            }
        }
        stats.dom.style.cssText = `
            position: fixed;
            top: 0;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1000; // Adjust if necessary
            `;
    }

    public ensureCanvasAttached() {
        this.attachCanvasToContainer();
    }

    public getScene() {
        return this.scene;
    }

    public getInstanceRenderer() {
        return this._instanceRenderer;
    }
    /**
     * @deprecated this should be removed
     */
    private initializeSetters() {
        setRenderer(this.renderer);
        setCamera(this.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type));
        setPostProcess(this.postProcess);
        setScene(this.scene);
    }

    /**
     * MUST RUN AFTER INTIALIZATION
     */
    async initialize() {
        this.bindPipelineRender();
        this.postProcess.applyPostProcess();
        this.scene.background = new THREE.Color(getColorScheme().SKY);

        const fpsIndicatorVisible = getGlobalState().overlays.fpsIndicatorVisible;
        this.setStatsVisibility(fpsIndicatorVisible);

        // Init ShadowBuilder (When Grid is initialized):
        const shadowBuilder = new ShadowBuilder(this.renderer, this.scene);
        shadowBuilder.setCamera(this.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type));
        this.shadowBuilder = shadowBuilder;

        this.standardRenderer = new StandardRenderer();
        console.warn('RUNNING RUN2');

        this.setDrawingSettings();

        document.getElementById('canvas-container')?.appendChild(this.canvas);

        this.initializeSetters();
        this.initTransformControls(this.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type));

        // Initialization of the InstanceRenderer
        this._instanceRenderer = new InstanceRenderer();

        // Draw Geometry:

        getGrid().contextItems?.addToScene(this.scene);
        await VisualizationElements.loadGeometries(this.scene);

        this.postProcess.applyPostProcess();

        // Small hydratation for framemax
        const grid = getGrid();
        const activeLayer = grid.getActiveLayer();
        usePersistentStore.getState().setSegmentsPanel({ frameMax: activeLayer.frameMax ?? 0 });

        this.cameraHelper.setCameraMode(getGlobalState().ambient.currentCamera, getGlobalState().ambient.currentCamera.type, true);

        this.renderReady = true;
        this.bindActivityRenderEvents();
        this.bindRaysRender();
        setLoadingStatus(LoadingStatus.VISUALIZER_READY);
        this.requestRenderIfNotRequested();
    }

    ////////////////////
    // RefreshFrames //
    //////////////////

    // Public API for React/UI
    /**
     * @deprecated you should use the pipeline controller and run it from there!
     */
    public refreshFrames(): void {
        ControllerManager.getInstance().getPipelineController().run();
    }
    public updateSensors(pipelineResult?: PipelineExecutionResult) {
        try {
            this._instanceRenderer?.updateFrames(pipelineResult);
        } catch (e) {
            console.log(e);
        } finally {
            this.requestRenderIfNotRequested();
        }
    }
    ////////////////////
    // getFrameDraw ///
    //////////////////

    // Expose frameDraw for transparency
    public getFrameDraw() {
        return this._instanceRenderer?.getSingleDraw();
    }

    //////////////
    // Three.js //
    //////////////

    initTransformControls(camera: THREE.Camera) {
        // Now initialize TransformControls with the random mesh as its target
        const transformControls = new TransformControls(camera, this.renderer.domElement);
        transformControls.getHelper().rotateX(Math.PI / 2);
        transformControls.enabled = false;

        transformControls.addEventListener('change', this.requestRenderIfNotRequested);
        transformControls.addEventListener('dragging-changed', (event) => {
            console.log(event);
            this.cameraHelper.getOrbitControls().enableRotate = !event.value;
            this.cameraHelper.getOrbitControls().update();
            this.cameraHelper.getTrackballControls().enabled = !event.value;
            this.cameraHelper.getTrackballControls().update();
            dragging = event.value as boolean;
        });

        this.scene.add(transformControls.getHelper());
        this.transformControls = transformControls; // Store in Zustand within Viewer Store (as class member of viewer).
    }

    initWindow() {
        // document.body.appendChild(this.canvas);

        // Ensure canvas size is updated on window resize
        window.addEventListener('resize', this.handleResize);

        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    //Resize canvas with current active camera
    updateCanvasSize() {
        if (!this.renderer || this.disposed) return;

        const activeCamera = this.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type);
        if (activeCamera instanceof THREE.PerspectiveCamera) {
            activeCamera.aspect = window.innerWidth / window.innerHeight;
            activeCamera.updateProjectionMatrix();
        } else if (activeCamera instanceof THREE.OrthographicCamera) {
            const orthographicDivision = 8;
            activeCamera.left = window.innerWidth / -orthographicDivision;
            activeCamera.right = window.innerWidth / orthographicDivision;
            activeCamera.top = window.innerHeight / orthographicDivision;
            activeCamera.bottom = window.innerHeight / -orthographicDivision;
            activeCamera.updateProjectionMatrix();
        }

        this.renderer.setSize(window.innerWidth, window.innerHeight, true);
        this.renderer.setSize(window.innerWidth * window.devicePixelRatio, window.innerHeight * window.devicePixelRatio, false);
        this.postProcess.applyPostProcess(undefined, activeCamera);
        this.shadowBuilder?.setCamera(activeCamera);
    }

    updateBackground() {
        (this.scene.background as THREE.Color).set(getColorScheme().SKY);
        this.requestRenderIfNotRequested();
    }

    async setDrawingSettings() {
        // Scene:
    }

    computeFPS(time: number) {
        const deltaMs = time - lastTime;
        if (!lastTime || deltaMs > 200) {
            // Avoid huge deltas after idle time to prevent jumpy camera motion.
            lastTime = time;
            this._deltaSec = 0;
            return;
        }

        lastTime = time;
        this._deltaSec = Math.min(Math.max(deltaMs / 1000, 1e-8), 1);

        const instantFPS = 1 / this._deltaSec;

        this.fps = Math.round(this.fps * 0.8 + instantFPS * 0.2); // smooth
    }

    cameraSettings() {
        const currentCameraMode = getGlobalState().ambient.currentCamera;
        const currentCamera = this.cameraHelper.getActiveCamera(currentCameraMode.type);
        const controls = this.cameraHelper.getOrbitControls();
        if (currentCamera instanceof THREE.PerspectiveCamera || currentCamera instanceof THREE.OrthographicCamera) {
            if (worldStatus.ambient.FAR_PLANE != currentCamera.far) {
                if (controls.object instanceof THREE.PerspectiveCamera) {
                    controls.object.far = worldStatus.ambient.FAR_PLANE;
                    controls.object.updateProjectionMatrix();
                }
                currentCamera.updateMatrix();
            }
        }
    }

    loop(time: number) {
        this.computeFPS(time);
        // Update camera settings:
        this.standardRenderer?.updateContext();
        if (!dragging) this.cameraSettings();

        // Camera Wasd

        this.cameraHelper.update(this._deltaSec);
        const activeCamera = this.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type);
        this.shadowBuilder?.updateForFrame(activeCamera);

        // Regular rendering (before post-processing)
        // Regular render scene with camera (this is the important part)
        // globalState.renderer.render(globalState.scene, globalState.camera); // Ensure this is being called
        // Then post-process with composer:
        this.postProcess.composer.render();
        this.cacheFrameForSentry(time);
        this.renderCount += 1;

        this.renderer.state.reset(); // Prevent flickering of rays

        // Update stats:
        stats.update();

        if (this.continuousRender) {
            this.requestRenderIfNotRequested();
        } else {
            this.scheduleBurstRender();
        }
    }

    done() {
        if (this.disposed) return;
        this.disposed = true;
        this.renderReady = false;
        void ControllerManager.getInstance().getStateController().uploadAutomaticStateNow();

        this.selectMode?.disable();

        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        this.unbindActivityRenderEvents();
        this.unbindPipelineRender();
        this.unbindRaysRender();

        this.renderer.setAnimationLoop(null);
        if (this.rafId != null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.burstTimeoutId != null) {
            clearTimeout(this.burstTimeoutId);
            this.burstTimeoutId = null;
        }
        this.unbindRenderEvents();

        if (localStorage.getItem('<grid_id>') !== null) {
            this.cameraHelper.refreshStore(getGlobalState().ambient.currentCamera.type);
        }
        this.shadowBuilder?.dispose();
        this.transformControls?.dispose();
        this._instanceRenderer?.dispose();

        if (stats.dom.parentNode) {
            stats.dom.parentNode.removeChild(stats.dom);
        }

        this.renderer.forceContextLoss();
        this.renderer.dispose();
        this.canvas.remove();
        this.postProcess.dispose();
        if (this.canvas?.parentNode) {
            this.canvas.parentNode.removeChild(this.canvas);
        }
        this.scene.clear();
        this.cameraHelper.dispose();
    }

    public getRenderCount() {
        return this.renderCount;
    }

    private bindRenderEvents() {
        /**A little repetitive because in CameraHelper update function() */

        const orbitControls = this.cameraHelper.getOrbitControls();
        orbitControls.addEventListener('change', this.handleControlRender);
        orbitControls.addEventListener('start', this.handleControlRender);
        orbitControls.addEventListener('end', this.handleControlRender);

        const trackballControls = this.cameraHelper.getTrackballControls();
        trackballControls.addEventListener('change', this.handleControlRender);
        trackballControls.addEventListener('start', this.handleControlRender);
        trackballControls.addEventListener('end', this.handleControlRender);
    }

    private unbindRenderEvents() {
        const orbitControls = this.cameraHelper.getOrbitControls();
        orbitControls.removeEventListener('change', this.handleControlRender);
        orbitControls.removeEventListener('start', this.handleControlRender);
        orbitControls.removeEventListener('end', this.handleControlRender);

        const trackballControls = this.cameraHelper.getTrackballControls();
        trackballControls.removeEventListener('change', this.handleControlRender);
        trackballControls.removeEventListener('start', this.handleControlRender);
        trackballControls.removeEventListener('end', this.handleControlRender);
    }

    private bindActivityRenderEvents() {
        if (this.activityEventsBound) return;
        this.activityEventsBound = true;

        const options: AddEventListenerOptions = { passive: true };
        window.addEventListener('pointerdown', this.handleUiPointerDown, options);
        window.addEventListener('pointerup', this.handleUiPointerUp, options);
        window.addEventListener('pointercancel', this.handleUiPointerUp, options);
        window.addEventListener('click', this.handleUiRender, options);
        window.addEventListener('wheel', this.handleUiRender, options);
        window.addEventListener('keydown', this.handleUiRender, options);
        window.addEventListener('keyup', this.handleUiRender, options);
        window.addEventListener('touchstart', this.handleUiPointerDown, options);
        window.addEventListener('touchend', this.handleUiPointerUp, options);
        window.addEventListener('touchcancel', this.handleUiPointerUp, options);
    }

    private unbindActivityRenderEvents() {
        if (!this.activityEventsBound) return;
        this.activityEventsBound = false;

        window.removeEventListener('pointerdown', this.handleUiPointerDown);
        window.removeEventListener('pointerup', this.handleUiPointerUp);
        window.removeEventListener('pointercancel', this.handleUiPointerUp);
        window.removeEventListener('click', this.handleUiRender);
        window.removeEventListener('wheel', this.handleUiRender);
        window.removeEventListener('keydown', this.handleUiRender);
        window.removeEventListener('keyup', this.handleUiRender);
        window.removeEventListener('touchstart', this.handleUiPointerDown);
        window.removeEventListener('touchend', this.handleUiPointerUp);
        window.removeEventListener('touchcancel', this.handleUiPointerUp);
    }

    private bindPipelineRender() {
        if (this.pipelineUnsub) return;
        const pipelineController = ControllerManager.getInstance().getPipelineController();
        this.pipelineUnsub = pipelineController.onRun(this.handleUiRender);
    }

    private unbindPipelineRender() {
        if (!this.pipelineUnsub) return;
        this.pipelineUnsub();
        this.pipelineUnsub = null;
    }

    private bindRaysRender() {
        if (this.raysUnsub) return;
        this.raysUnsub = RaysHandler.onRaysChanged(this.handleUiRender);
    }

    private unbindRaysRender() {
        if (!this.raysUnsub) return;
        this.raysUnsub();
        this.raysUnsub = null;
    }

    //Function that paces frames
    private scheduleBurstRender() {
        if (this.disposed || this.continuousRender) return;
        if (this.burstTimeoutId != null) return;
        if (this.burstUntil <= performance.now()) return;

        const intervalMs = Math.max(1, Math.floor(1000 / Math.max(this.burstFps, 1)));
        this.burstTimeoutId = window.setTimeout(() => {
            this.burstTimeoutId = null;
            this.requestRenderIfNotRequested();
        }, intervalMs);
    }

    private cacheFrameForSentry(time: number) {
        if (time - this.lastSentryFrameCacheTs < Visualization.SENTRY_FRAME_CACHE_INTERVAL_MS) return;
        if (!this.canvas.width || !this.canvas.height) return;

        try {
            cacheCanvasFrame(this.canvas.toDataURL('image/png'));
            this.lastSentryFrameCacheTs = time;
        } catch (error) {
            console.warn('Unable to cache renderer frame for Sentry.', error);
        }
    }
}
